// ai-sidebar/suggestions.ts
// Contextual launchpad content. Per spec section 9, the "always listening"
// feel is a UI quality, not background LLM compute: greeting + suggested
// asks/dos are precomputed, rule-based, and swap with the current page. No
// model call fires until the user actually sends one.

import type { Phase, Suggestion } from "./types";

interface PageContent {
  /** Short status line under the assistant name. */
  status: string;
  /** Launchpad greeting headline. */
  greeting: string;
  suggestions: Suggestion[];
}

// Suggestions lead with uncovering insight and communicating it. Tone is
// constructive and opportunity-focused, never alarming. 3 questions + 2 actions
// per page, and the actions vary by page so they don't read as boilerplate.
const GENERAL: PageContent = {
  status: "Ask about this page",
  greeting: "What can I help you understand?",
  suggestions: [
    { kind: "ask", label: "What's most important on this page?" },
    { kind: "ask", label: "What's working well here?" },
    { kind: "ask", label: "Where should I focus next?" },
    { kind: "do", label: "Write an email summary for leadership" },
    { kind: "do", label: "Pull out the key takeaways" },
  ],
};

const BY_PAGE: Partial<Record<Phase, PageContent>> = {
  antenna: {
    status: "Reading your baseline",
    greeting: "Let's dig into your AI perception baseline.",
    suggestions: [
      { kind: "ask", label: "What's shaping my perception score?" },
      { kind: "ask", label: "How do the models compare on my brand?" },
      { kind: "ask", label: "Where's my biggest opportunity to improve?" },
      { kind: "do", label: "Write a leadership email on this run" },
      { kind: "do", label: "Draft talking points on my AI perception" },
    ],
  },
  cognition: {
    status: "Comparing how models think",
    greeting: "Let's compare how the models reason about your brand.",
    suggestions: [
      { kind: "ask", label: "How do the models understand my brand?" },
      { kind: "ask", label: "What concepts do they associate with me?" },
      { kind: "ask", label: "Where could I align them better?" },
      { kind: "do", label: "Summarize the model differences for my team" },
      { kind: "do", label: "Turn these findings into slide ideas" },
    ],
  },
  "fix-actions": {
    status: "Reading your priorities",
    greeting: "Let's find the highest-leverage moves.",
    suggestions: [
      { kind: "ask", label: "What's worth tackling first?" },
      { kind: "ask", label: "Where's the quickest meaningful win?" },
      { kind: "ask", label: "What would have the biggest impact?" },
      { kind: "do", label: "Draft a prioritized action list" },
      { kind: "do", label: "Write a quick update for the team" },
    ],
  },
  fix: {
    status: "Reading your priorities",
    greeting: "Let's find the highest-leverage moves.",
    suggestions: [
      { kind: "ask", label: "What's worth tackling first?" },
      { kind: "ask", label: "Where's the quickest meaningful win?" },
      { kind: "ask", label: "What would have the biggest impact?" },
      { kind: "do", label: "Draft a prioritized action list" },
      { kind: "do", label: "Write a quick update for the team" },
    ],
  },
  "prompt-tracking": {
    status: "Watching your prompts",
    greeting: "Let's see where your prompts can grow.",
    suggestions: [
      { kind: "ask", label: "Which prompts have the most upside?" },
      { kind: "ask", label: "Where could I show up earlier?" },
      { kind: "ask", label: "Which prompts am I strongest on?" },
      { kind: "do", label: "Write a one-paragraph coverage summary" },
      { kind: "do", label: "Suggest prompts worth adding" },
    ],
  },
  insights: {
    status: "Reading your insights",
    greeting: "Let's make these insights tell a story.",
    suggestions: [
      { kind: "ask", label: "What story do these insights tell?" },
      { kind: "ask", label: "Which insight is most compelling?" },
      { kind: "ask", label: "What would strengthen the picture?" },
      { kind: "do", label: "Draft an executive email from these insights" },
      { kind: "do", label: "Outline a board-ready narrative" },
    ],
  },
  "report-generator": {
    status: "Reading your data",
    greeting: "Let's shape the strongest narrative.",
    suggestions: [
      { kind: "ask", label: "What's the strongest narrative in my data?" },
      { kind: "ask", label: "What should lead the report?" },
      { kind: "ask", label: "What would a board want to see?" },
      { kind: "do", label: "Outline an executive summary for a board" },
      { kind: "do", label: "Suggest slides for a leadership update" },
    ],
  },
  results: {
    status: "Reading your results",
    greeting: "Let's make sense of these results.",
    suggestions: [
      { kind: "ask", label: "What's the headline from these results?" },
      { kind: "ask", label: "What improved the most?" },
      { kind: "ask", label: "Where should I focus next?" },
      { kind: "do", label: "Write a results recap for leadership" },
      { kind: "do", label: "Pull out the top highlights" },
    ],
  },
  sandbox: {
    status: "Reading your scenarios",
    greeting: "Let's explore your test scenarios.",
    suggestions: [
      { kind: "ask", label: "What can I test in the sandbox?" },
      { kind: "ask", label: "How would a messaging change play out?" },
      { kind: "ask", label: "Which scenario is worth trying first?" },
      { kind: "do", label: "Summarize my sandbox findings" },
      { kind: "do", label: "Draft a recommendation from these scenarios" },
    ],
  },
  "deep-signal": {
    status: "Reading the deep signal",
    greeting: "Let's dig into the deeper signal.",
    suggestions: [
      { kind: "ask", label: "What's the strongest signal here?" },
      { kind: "ask", label: "What stands out most?" },
      { kind: "ask", label: "Where's the biggest opportunity?" },
      { kind: "do", label: "Write talking points on the key signal" },
      { kind: "do", label: "Turn this into slide ideas" },
    ],
  },
  analytics: {
    status: "Reading your trends",
    greeting: "Let's make sense of your trends over time.",
    suggestions: [
      { kind: "ask", label: "How has my perception changed over time?" },
      { kind: "ask", label: "What's trending up?" },
      { kind: "ask", label: "What's worth watching?" },
      { kind: "do", label: "Write a trend update for leadership" },
      { kind: "do", label: "Draft a 'what changed' recap" },
    ],
  },
  "brand-voice": {
    status: "Reading your brand voice",
    greeting: "Let's see how your voice reaches AI.",
    suggestions: [
      { kind: "ask", label: "How well does my brand voice reach AI?" },
      { kind: "ask", label: "What's coming through clearly?" },
      { kind: "ask", label: "Where could my voice be stronger?" },
      { kind: "do", label: "Summarize my brand-voice notes" },
      { kind: "do", label: "Draft messaging guidance from this" },
    ],
  },
  configure: {
    status: "Setting up your analysis",
    greeting: "Let's get your analysis set up.",
    suggestions: [
      { kind: "ask", label: "Which models should I track?" },
      { kind: "ask", label: "How specific should my brand search be?" },
      { kind: "ask", label: "What will the analysis tell me?" },
      { kind: "do", label: "Summarize my latest run for leadership" },
      { kind: "do", label: "Draft a quick status update" },
    ],
  },
};

export function pageContent(phase: Phase): PageContent {
  return BY_PAGE[phase] ?? GENERAL;
}

// Human-friendly page names, sent to the agent so answers can be page-aware.
const PAGE_LABELS: Partial<Record<Phase, string>> = {
  antenna: "Antenna (AI perception dashboard)",
  cognition: "Cognition",
  "fix-actions": "Fix Actions",
  fix: "Fix Actions",
  sandbox: "Sandbox",
  "prompt-tracking": "Prompt Tracking",
  insights: "Insights & Reports",
  "report-generator": "Report Generator",
  results: "Results",
  configure: "Configure (brand search)",
  preview: "Preview",
  "deep-signal": "Deep Signal",
  analytics: "Analytics (trends over time)",
  "brand-voice": "Brand Voice",
};

export function pageLabel(phase: Phase): string {
  return PAGE_LABELS[phase] ?? "this page";
}
